import { useEffect, useRef, useState } from "react";
import { motion as m } from "motion/react";
import type { Player, RoomView, Team } from "../../shared/game-types.js";
import { useMotionTuning, useTheme } from "../lib/tuning.js";

/** Animate a score to its new value rather than snapping — points should feel earned. */
export function ScoreNumber({ value }: { value: number }) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  const motion = useMotionTuning();

  useEffect(() => {
    const start = performance.now();
    const origin = from.current;
    const delta = value - origin;
    if (delta === 0) return;
    const duration = motion.score.countUpDuration_ms;
    let raf = 0;

    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // Ease-out cubic: fast commitment, soft landing.
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(Math.round(origin + delta * eased));
      if (t < 1) raf = requestAnimationFrame(step);
      else from.current = value;
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, motion.score.countUpDuration_ms]);

  return (
    <m.div
      className="team__score numeral"
      key={value}
      animate={{ scale: [1, motion.score.popScale, 1] }}
      transition={{ duration: motion.score.popDuration_ms / 1000 }}
    >
      {shown}
    </m.div>
  );
}

export function TeamPanel({
  team,
  players,
  active,
  side,
}: {
  team: Team;
  players: Player[];
  active: boolean;
  side: "left" | "right";
}) {
  const roster = players.filter((p) => p.teamId === team.id);
  return (
    <div
      className={`team teamTint ${side === "right" ? "team--right" : ""} ${active ? "team--active" : ""}`}
      data-team={team.id}
    >
      <div>
        <div className="team__name">{team.name}</div>
        <div className="team__roster">
          {roster.length ? roster.map((p) => p.name).join(" · ") : "waiting"}
        </div>
      </div>
      {/* Before the score, not after it: with `space-between` the last item is
          pinned to the outer edge, and the score must not jump sideways every
          time the turn changes hands. */}
      {active ? <div className="team__turn">on the clock</div> : null}
      <ScoreNumber value={team.score} />
    </div>
  );
}

export function TimerRing({
  remaining,
  total,
}: {
  remaining: number;
  total: number;
}) {
  const theme = useTheme();
  const motion = useMotionTuning();
  const size = 96;
  const thickness = motion.timer.ringThickness_px;
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? Math.max(0, Math.min(1, remaining / total)) : 0;
  const urgent = remaining <= motion.timer.urgentBelow_s && remaining > 0;

  return (
    <div className="ring">
      <m.svg
        width={size}
        height={size}
        animate={urgent ? { scale: [1, 1.06, 1] } : { scale: 1 }}
        transition={
          urgent
            ? { duration: motion.timer.urgentPulse_ms / 1000, repeat: Infinity }
            : { duration: 0.2 }
        }
      >
        {/* Inked dial: a filled disc, a heavy outline, and the remaining time
            drawn on top as a thick arc — closer to a printed gauge than a ring. */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill={theme.palette.card}
          stroke={theme.palette.ink}
          strokeWidth={theme.print.strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={urgent ? theme.palette.rose : theme.palette.affirm}
          strokeWidth={thickness}
          strokeLinecap="butt"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - progress)}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{
            transition: "stroke-dashoffset 120ms linear, stroke 240ms ease",
          }}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={theme.palette.ink}
          strokeWidth={theme.print.strokeWidth}
        />
      </m.svg>
      <div className="ring__num numeral">{Math.ceil(remaining)}</div>
    </div>
  );
}

export function Strikes({ count, limit }: { count: number; limit: number }) {
  return (
    <div className="strikes" aria-label={`${count} of ${limit} strikes`}>
      {Array.from({ length: limit }, (_, i) => (
        <i key={i} data-on={i < count} />
      ))}
    </div>
  );
}

export function Hud({
  room,
  remaining,
  answerSeconds,
}: {
  room: RoomView;
  remaining: number;
  answerSeconds: number;
}) {
  const activePlayer = room.players.find((p) => p.id === room.activePlayerId);
  const live = room.phase === "playing";

  return (
    <div className="hud">
      <TeamPanel
        team={room.teams.rose}
        players={room.players}
        active={live && room.activeTeam === "rose"}
        side="left"
      />

      <div className="turn teamTint" data-team={room.activeTeam}>
        {live ? (
          <>
            <TimerRing remaining={remaining} total={answerSeconds} />
            <m.div
              className="turn__who"
              key={activePlayer?.id ?? "none"}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {activePlayer?.name ?? "—"}
            </m.div>
            <Strikes count={room.strikes} limit={room.strikeLimit} />
          </>
        ) : (
          <div className="turn__hint">
            {room.phase === "category-reveal"
              ? "Category closed"
              : room.phase === "category-intro"
                ? "Get ready"
                : ""}
          </div>
        )}
      </div>

      <TeamPanel
        team={room.teams.slate}
        players={room.players}
        active={live && room.activeTeam === "slate"}
        side="right"
      />
    </div>
  );
}
